/**
 * Утилита для сжатия изображений на клиенте перед загрузкой
 * Использует Canvas API для сжатия и изменения размера
 */

export interface CompressionOptions {
  maxWidth?: number;        // Максимальная ширина (по умолчанию 1920px)
  maxHeight?: number;       // Максимальная высота (по умолчанию 1920px)
  quality?: number;         // Качество изображения (0-1, по умолчанию 0.85)
  maxSizeMB?: number;       // Максимальный размер файла в MB (по умолчанию 2MB)
  outputFormat?: 'image/jpeg' | 'image/webp' | 'image/png'; // Формат вывода (по умолчанию WebP, если поддерживается)
}

const DEFAULT_OPTIONS: Required<Omit<CompressionOptions, 'outputFormat'>> & { outputFormat: 'image/webp' | 'image/jpeg' } = {
  maxWidth: 1920,
  maxHeight: 1920,
  quality: 0.85,
  maxSizeMB: 2,
  outputFormat: 'image/webp' // WebP по умолчанию
};

/**
 * Проверяет поддержку WebP в браузере
 */
function isWebPSupported(): boolean {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  } catch {
    return false;
  }
}

/**
 * Определяет оптимальный формат вывода
 */
function getOptimalFormat(preferredFormat?: 'image/jpeg' | 'image/webp' | 'image/png'): 'image/webp' | 'image/jpeg' {
  if (preferredFormat === 'image/png') {
    return 'image/jpeg'; // PNG не подходит для сжатия фото
  }
  
  if (preferredFormat === 'image/jpeg') {
    return 'image/jpeg';
  }
  
  // По умолчанию используем WebP, если поддерживается, иначе JPEG
  return isWebPSupported() ? 'image/webp' : 'image/jpeg';
}

/**
 * Создает изображение из файла
 */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Вычисляет новые размеры изображения с сохранением пропорций
 */
function calculateDimensions(
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  if (width <= maxWidth && height <= maxHeight) {
    return { width, height };
  }

  const ratio = Math.min(maxWidth / width, maxHeight / height);
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio)
  };
}

/**
 * Сжимает изображение с использованием Canvas API
 */
function compressImage(
  image: HTMLImageElement,
  options: Required<Omit<CompressionOptions, 'outputFormat'>> & { outputFormat: 'image/webp' | 'image/jpeg' }
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Не удалось создать canvas context'));
      return;
    }

    // Вычисляем новые размеры
    const { width, height } = calculateDimensions(
      image.width,
      image.height,
      options.maxWidth,
      options.maxHeight
    );

    canvas.width = width;
    canvas.height = height;

    // Рисуем изображение на canvas
    ctx.drawImage(image, 0, 0, width, height);

    // Сжимаем с учетом максимального размера
    const compressWithQuality = (quality: number): void => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Не удалось создать blob'));
            return;
          }

          const sizeMB = blob.size / (1024 * 1024);

          // Если размер меньше максимального или качество уже минимальное - возвращаем
          // Минимальное качество увеличено до 0.7 (70%) для лучшего качества изображений
          if (sizeMB <= options.maxSizeMB || quality <= 0.7) {
            resolve(blob);
            return;
          }

          // Уменьшаем качество меньшими шагами для более плавного сжатия
          compressWithQuality(quality - 0.05);
        },
        options.outputFormat,
        quality
      );
    };

    compressWithQuality(options.quality);
  });
}

/**
 * Сжимает изображение перед загрузкой
 * @param file Исходный файл изображения
 * @param options Опции сжатия
 * @returns Promise с сжатым файлом (Blob, который можно использовать как File)
 */
export async function compressImageFile(
  file: File,
  options: CompressionOptions = {}
): Promise<File> {
  // Определяем оптимальный формат (WebP если поддерживается, иначе JPEG)
  const optimalFormat = getOptimalFormat(options.outputFormat);
  const webpSupported = isWebPSupported();
  
  console.log(`Сжатие изображения: ${file.name}, WebP поддерживается: ${webpSupported}, формат: ${optimalFormat}`);
  
  const opts = { 
    ...DEFAULT_OPTIONS, 
    ...options,
    outputFormat: optimalFormat
  } as Required<Omit<CompressionOptions, 'outputFormat'>> & { outputFormat: 'image/webp' | 'image/jpeg' };

  // Если файл уже достаточно маленький, возвращаем как есть
  const fileSizeMB = file.size / (1024 * 1024);
  if (fileSizeMB <= opts.maxSizeMB) {
    // Но все равно проверяем размеры изображения
    const image = await loadImage(file);
    if (image.width <= opts.maxWidth && image.height <= opts.maxHeight) {
      return file;
    }
  }

  try {
    // Загружаем изображение
    const image = await loadImage(file);

    // Сжимаем
    const compressedBlob = await compressImage(image, opts);

    // Освобождаем память
    URL.revokeObjectURL(image.src);

    // Определяем расширение файла в зависимости от формата
    const extension = opts.outputFormat === 'image/webp' ? '.webp' : '.jpg';
    const fileName = file.name.replace(/\.[^/.]+$/, '') + extension;
    
    const compressedFile = new File(
      [compressedBlob],
      fileName,
      {
        type: opts.outputFormat,
        lastModified: Date.now()
      }
    );

    const formatName = opts.outputFormat === 'image/webp' ? 'WebP' : 'JPEG';
    console.log(`Изображение сжато (${formatName}): ${(file.size / 1024 / 1024).toFixed(2)}MB -> ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);

    return compressedFile;
  } catch (error) {
    console.error('Ошибка сжатия изображения:', error);
    // В случае ошибки возвращаем оригинальный файл
    return file;
  }
}

/**
 * Сжимает массив изображений
 */
export async function compressImageFiles(
  files: File[],
  options: CompressionOptions = {}
): Promise<File[]> {
  const compressedFiles = await Promise.all(
    files.map(file => compressImageFile(file, options))
  );
  return compressedFiles;
}
